<template lang="pug">
.challenges-schedule
  //- Body always fills the slot so Spinner uses overlay mode (avoids jump / wrong position while loading).
  NativeAppSpinnerWrapper(:active="loading" :overlayFixed="true" :overlayZIndex="100")
    .schedule-body
      .cms-content(v-if="contentBlocks.length > 0")
        RenderBlock(
          v-for="(block, index) in contentBlocks"
          :key="`content-${index}`"
          :block="block"
        )
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useRogue } from '@/js/services/Rogue'
import { useCmsStore } from '@/js/stores/cmsStore'
import { messages } from '@/js/app-bridge/messages'
import NativeAppSpinnerWrapper from '../components/native-app-spinner-wrapper.vue'
import RenderBlock from 'sisu/components/RenderBlock'

const router = useRouter()
const rogue = useRogue()
const cmsStore = useCmsStore()

const loading = ref(true)
const cmsPage = ref<any>(null)

// CMS page URL for the schedule tab
const CMS_PAGE_URL = '/challenges/challenge-schedule'

// All CMS content blocks - render everything from CMS including tabs
const contentBlocks = computed(() => {
  if (!cmsPage.value?.roguebuilder) {
    return []
  }

  const rb = cmsPage.value.roguebuilder

  if (Array.isArray(rb)) {
    return rb
  } else if (rb.slots?.default) {
    return rb.slots.default
  } else {
    return [rb]
  }
})

async function loadCmsContent() {
  loading.value = true
  try {
    // Resolve the URL to get the CMS ID
    const routeData = await rogue.api.fetch<{ cmsId?: number }>({
      url: `/svc_route/route?url=${CMS_PAGE_URL}`,
    })

    if (routeData?.cmsId) {
      const page = await cmsStore.load(routeData.cmsId, rogue)
      cmsPage.value = page
    }
  } catch (e) {
    rogue.log.error('Error loading schedule CMS content', e)
  }
  loading.value = false
}

onMounted(async () => {
  // Redirect browser users to the regular CMS page
  if (!rogue.appContext && !rogue.featureFlags.isOn('invitational_app_display')) {
    router.replace('/challenges')

    return
  }

  await loadCmsContent()
  if (rogue.appContext) {
    rogue.emitToApp(messages.DOM_LOADED, {})
  }
})
</script>

<style lang="scss" scoped>
.challenges-schedule {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: $color-white;
}

.schedule-body {
  flex: 1;
  min-height: 100vh;
  width: 100%;
}

.cms-content {
  flex: 1;
}
</style>

<style lang="scss" scoped target="app">
.platform-ios, .platform-android {
  .challenges-schedule {
    padding-top: 0;
  }

  .schedule-body .cms-content > div:first-child {
    margin-top: -$space-l;
  }
}
</style>
